/**
 * Context pour la gestion du profil acheteur (logo, documents, bibliographie)
 * Pattern identique à ProfileContext — chargé dès l'authentification si le rôle est acheteur.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

// ============================================
// Types
// ============================================

export interface AcheteurDocument {
    id: number;
    type: string;
    filename: string;
    updated_at: string;
    key: string;
}

export interface BibliographieDoc {
    id: number;
    nom: string;
    description?: string;
    taille?: number;
    updated_at: string;
}

export interface AcheteurCompletion {
    percent: number;
    is_complete: boolean;
    sections: {
        presentation: boolean;
        logo: boolean;
        documents: boolean;
    };
}

const DOCUMENT_TYPES = ['habilitation_admin', 'convention_deliberation'] as const;
export type AcheteurDocumentType = typeof DOCUMENT_TYPES[number];

const ACHETEUR_UI_ROLES = [
    'Acheteur signataire admin',
    'Acheteur signataire',
    'Acheteur',
];

interface AcheteurProfileContextState {
    logoUrl: string | null;
    documents: Record<string, AcheteurDocument | null>;
    bibliographie: BibliographieDoc[];
    completion: AcheteurCompletion | null;
    isLoading: boolean;
    setLogoUrl: (url: string | null) => void;
    setDocuments: React.Dispatch<React.SetStateAction<Record<string, AcheteurDocument | null>>>;
    setCompletion: React.Dispatch<React.SetStateAction<AcheteurCompletion | null>>;
    reloadDocuments: () => Promise<void>;
    reloadLogo: () => Promise<void>;
    reloadBibliographie: () => Promise<void>;
    refreshCompletion: () => Promise<void>;
}

const AcheteurProfileContext = createContext<AcheteurProfileContextState | null>(null);

// ============================================
// Hook
// ============================================

export const useAcheteurProfile = () => {
    const context = useContext(AcheteurProfileContext);
    if (!context) {
        throw new Error('useAcheteurProfile doit être utilisé dans un AcheteurProfileProvider');
    }
    return context;
};

// ============================================
// Provider
// ============================================

const emptyDocuments = (): Record<string, AcheteurDocument | null> => ({
    habilitation_admin: null,
    convention_deliberation: null,
});

const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('token');
    return {
        Authorization: token ? `Bearer ${token}` : '',
        Accept: 'application/json',
    };
};

export const AcheteurProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();

    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [documents, setDocuments] = useState<Record<string, AcheteurDocument | null>>(emptyDocuments());
    const [bibliographie, setBibliographie] = useState<BibliographieDoc[]>([]);
    const [completion, setCompletion] = useState<AcheteurCompletion | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const isAcheteur = isAuthenticated && user !== null && ACHETEUR_UI_ROLES.includes(user.role);

    // ============================================
    // Loaders
    // ============================================

    const reloadLogo = useCallback(async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiUrl}/acheteur-profile/logo`, {
                headers: getAuthHeaders(),
            });
            if (response.ok) {
                const data = await response.json();
                setLogoUrl(data.data?.logo_url || null);
            }
        } catch {
            // Logo non disponible, pas d'erreur bloquante
        }
    }, []);

    const reloadDocuments = useCallback(async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiUrl}/acheteur-profile/documents`, {
                headers: getAuthHeaders(),
            });
            if (response.ok) {
                const data = await response.json();
                const docs: AcheteurDocument[] = data.data || [];
                const byType = emptyDocuments();
                docs.forEach(doc => {
                    if (Object.prototype.hasOwnProperty.call(byType, doc.type)) {
                        byType[doc.type] = doc;
                    }
                });
                setDocuments(byType);
            }
        } catch {
            // Documents non disponibles, pas d'erreur bloquante
        }
    }, []);

    const reloadBibliographie = useCallback(async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(
                `${apiUrl}/documents-communs?type=bibliographie_acheteur&actif=true&per_page=100`,
                { headers: getAuthHeaders() }
            );
            if (response.ok) {
                const data = await response.json();
                const docs = data.data?.data ?? data.data ?? [];
                setBibliographie(Array.isArray(docs) ? docs : []);
            }
        } catch {
            // Bibliographie non disponible, pas d'erreur bloquante
        }
    }, []);

    const refreshCompletion = useCallback(async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiUrl}/acheteur-profile/completion`, {
                headers: getAuthHeaders(),
            });
            if (response.ok) {
                const data = await response.json();
                setCompletion(data.data ?? null);
            }
        } catch {
            // Completion non disponible, pas d'erreur bloquante
        }
    }, []);

    // ============================================
    // Chargement automatique dès l'authentification (rôle acheteur uniquement)
    // ============================================

    useEffect(() => {
        if (isAcheteur) {
            setIsLoading(true);
            Promise.all([reloadLogo(), reloadDocuments(), reloadBibliographie()]).finally(() => {
                setIsLoading(false);
            });
        } else {
            setLogoUrl(null);
            setDocuments(emptyDocuments());
            setBibliographie([]);
            setCompletion(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAcheteur]);

    // Completion (pastille sidebar) : effet DEDIE, independant du Promise.all ci-dessus, pour qu'un
    // echec/throw d'un autre chargement (logo/documents) ne l'empeche jamais de partir. Charge des
    // l'authentification acheteur -> la pastille est a jour sans devoir passer par l'onglet Profil.
    useEffect(() => {
        if (isAcheteur) {
            refreshCompletion();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAcheteur]);

    // ============================================
    // Valeur du contexte
    // ============================================

    const value: AcheteurProfileContextState = {
        logoUrl,
        documents,
        bibliographie,
        completion,
        isLoading,
        setLogoUrl,
        setDocuments,
        setCompletion,
        reloadDocuments,
        reloadLogo,
        reloadBibliographie,
        refreshCompletion,
    };

    return (
        <AcheteurProfileContext.Provider value={value}>
            {children}
        </AcheteurProfileContext.Provider>
    );
};

export default AcheteurProfileContext;
